import type { ComponentType } from 'react';
import {
  Bot,
  Building2,
  CheckIcon,
  Hash,
  Key,
  MessageSquare,
  User,
  Users,
  type LucideIcon,
} from 'lucide-react';
import { ENTITY_ID_SCANNER_PATTERN, parseId } from '@runloop/reflex-contract';
import { CORE_IDS } from '@reflex/shared';
import type { ContentScannerComponentProps } from '../../client/content-scanners';
import { useCopyToClipboard } from '../../hooks/useCopyToClipboard';
import { useOptionalRegisteredComponent } from '../registry/PluginComponentRegistry';

/**
 * Compiled form of the shared {@link ENTITY_ID_SCANNER_PATTERN}: matches a
 * prefixed entity id (e.g. `agt_V1StGXR8Z5jdHi6BmyT8aQ`) with its prefix and
 * body as capture groups 1 and 2, so the badge can label the id by kind without
 * re-parsing. The chat plugin registers the pattern string as a `text` content
 * scanner; this compiled copy backs the badge component's own tests/stories.
 */
export const ENTITY_ID_SCANNER_REGEX = new RegExp(ENTITY_ID_SCANNER_PATTERN);

/**
 * Shared chip styling for the entity-id badge. Exported so the host badge
 * (which wraps the chip in a hover preview) renders an identical trigger
 * without duplicating the class string.
 */
export const ENTITY_ID_BADGE_CHIP_CLASS =
  'group mx-px inline-flex max-w-full items-center gap-1 rounded-md border border-border/60 bg-muted/40 px-1.5 py-0.5 align-middle font-mono text-xs leading-none text-muted-foreground transition-colors hover:border-border hover:bg-muted hover:text-foreground';

/** Reverse of `CORE_IDS`: 3-letter prefix -> entity kind name. */
const CORE_KIND_BY_PREFIX: Record<string, string> = Object.fromEntries(
  Object.entries(CORE_IDS).map(([kind, prefix]) => [prefix, kind]),
);

/** Kind-specific glyphs for the common core entities; everything else gets a hash. */
const ICON_BY_PREFIX: Record<string, LucideIcon> = {
  [CORE_IDS.agent]: Bot,
  [CORE_IDS.agentMessage]: MessageSquare,
  [CORE_IDS.user]: User,
  [CORE_IDS.organization]: Building2,
  [CORE_IDS.team]: Users,
  [CORE_IDS.secret]: Key,
};

/** `agentMessage` -> `agent message`, `team` -> `team`. */
function humanizeKind(kind: string): string {
  return kind
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
    .toLowerCase();
}

/** Human label for a prefix from the core kind map, if known. */
function entityKindLabelForPrefix(prefix: string): string | undefined {
  const kind = CORE_KIND_BY_PREFIX[prefix];
  return kind ? humanizeKind(kind) : undefined;
}

/**
 * Presentational, copy-only chip for a prefixed entity id. Renders the id as a
 * compact monospace chip with a kind glyph, the way GitHub badges a commit SHA,
 * and copies the full id to the clipboard on click. This is the fallback the
 * scanner renders when no host preview is registered (Storybook, plugin pages,
 * or a prefix no preview owns). `icon` overrides the derived core glyph so the
 * host can supply a registry-resolved icon.
 */
export function EntityIdBadgeChip({
  match,
  groups,
  icon,
}: ContentScannerComponentProps & { icon?: ComponentType<{ className?: string }> }) {
  const id = match;
  const prefix = groups[0] || parseId(id)?.prefix || '';
  const label = entityKindLabelForPrefix(prefix);
  const Icon = icon ?? ICON_BY_PREFIX[prefix] ?? Hash;
  const { copied, copy } = useCopyToClipboard();
  const copyLabel = label ? `Copy ${label} id ${id}` : `Copy id ${id}`;

  return (
    <button
      type="button"
      onClick={() => copy(id)}
      aria-label={copyLabel}
      title={copied ? 'Copied!' : label ? `Copy ${label} id` : 'Copy id'}
      data-testid="entity-id-badge"
      data-copied={copied || undefined}
      className={ENTITY_ID_BADGE_CHIP_CLASS}
    >
      {copied ? (
        <CheckIcon className="size-3 shrink-0 text-emerald-500" aria-hidden="true" />
      ) : (
        <Icon className="size-3 shrink-0 opacity-70 group-hover:opacity-100" aria-hidden="true" />
      )}
      <span className="truncate select-all">{id}</span>
    </button>
  );
}

/**
 * Scanner-facing entity-id badge. Delegates to the host-registered `EntityIdBadge`
 * (icon + hover preview looked up by id type via the resource-preview registry)
 * when the web host has provided one; otherwise falls back to the plain
 * {@link EntityIdBadgeChip}. The chat plugin's `text` content scanner points at
 * this component, so a bare `agt_…`/`mps_…`/`dbx_…` in agent output becomes a
 * badge with the same hover card as an explicit resource chip.
 */
export function EntityIdBadge(props: ContentScannerComponentProps) {
  const Host = useOptionalRegisteredComponent('EntityIdBadge');
  if (Host) return <Host match={props.match} groups={props.groups} />;
  return <EntityIdBadgeChip {...props} />;
}
